import { ENDPOINT } from '@/services/endpoint';
import { InquiryService } from '@/services/inquiry';
import { useInquiryStore } from '@/store';
import {
  IGetListInquiryResponse,
  InquiryStatus,
} from '@/types/response/inquiry';
import { convertRequest, inquiryStatus, inquiryStatusColors } from '@/utils';
import { TableColumnsType, TableProps, Tag, Typography } from 'antd';
import { Table } from '@/components/commons';
import moment from 'moment';
import { useQuery } from 'react-query';

interface IParams {
  limit: number;
  page: number;
  title?: string;
}

export const InquiryTable = () => {
  const { filter, setParams, onOpenDetail } = useInquiryStore();

  const {
    data: list,
    refetch,
    isFetching,
  } = useQuery(
    [ENDPOINT.INQUIRY.GET_LIST, filter],
    () => InquiryService.getList(convertRequest(filter)),
    {
      select: (data) => data.data,
    },
  );

  const columns: TableColumnsType<IGetListInquiryResponse> = [
    {
      key: 'id',
      title: 'ID',
      dataIndex: 'id',
      align: 'center',
      width: '5%',
    },
    {
      key: 'email',
      title: 'メールアドレス',
      width: '20%',
      dataIndex: 'email',
    },
    {
      key: 'name',
      title: 'お名前',
      dataIndex: 'name',
      width: '20%',
    },
    {
      key: 'status',
      title: 'ステータス',
      width: '10%',
      dataIndex: 'status',
      align: 'center',
      render: (value: InquiryStatus) => (
        <Tag color={inquiryStatusColors[value]}>{inquiryStatus[value]}</Tag>
      ),
    },
    {
      key: 'content',
      title: 'お問い合わせ内容',
      width: '30%',
      dataIndex: 'content',
      render: (content) => (
        <Typography.Paragraph ellipsis={{ rows: 2 }} className='mb-0'>
          {content}
        </Typography.Paragraph>
      ),
    },
    {
      key: 'createdAt',
      title: '作成日時',
      width: '15%',
      dataIndex: 'createdAt',
      align: 'center',
      render: (createdAt) => moment(createdAt).format('YYYY年MM月DD日 HH:mm'),
    },
  ];

  const handleTableChange: TableProps['onChange'] = (
    pagination,
    filters,
    sorter,
    extra,
  ) => {
    if (extra.action !== 'sort') return;

    // Single or multiple sorter
    if (Array.isArray(sorter)) {
      // Pick the first active sorter (Antd supports multi sort but our API likely supports one)
      const active = sorter.find((s) => s.order);
      if (active && active.columnKey && active.order) {
        const sortDirection = active.order === 'ascend' ? 'ASC' : 'DESC';
        setParams({
          page: 1,
          sortBy: String(active.columnKey),
          sortDirection,
        } as any);
      } else {
        // Clear sorting
        setParams({
          page: 1,
          sortBy: undefined,
          sortDirection: undefined,
        } as any);
      }
    } else {
      if (sorter.order && sorter.columnKey) {
        const sortDirection = sorter.order === 'ascend' ? 'ASC' : 'DESC';
        setParams({
          page: 1,
          sortBy: String(sorter.columnKey),
          sortDirection,
        } as any);
      } else {
        // Clear sorting
        setParams({
          page: 1,
          sortBy: undefined,
          sortDirection: undefined,
        } as any);
      }
    }
  };

  const onChangePagination = (page: number, pageSize: number) => {
    setParams({ page, limit: pageSize });
  };

  return (
    <div className='flex justify-between flex-col h-full'>
      <Table
        className='break-all'
        rowKey='id'
        scroll={{ x: 800 }}
        bordered
        loading={isFetching}
        dataSource={list?.data || []}
        columns={columns}
        pagination={
          list?.pagination?.total! > (filter.limit ?? 10)
            ? {
                className: 'pagination-table',
                total: list?.pagination.total || 0,
                pageSize: filter.limit,
                current: filter.page,
                showSizeChanger: false,
                onChange: onChangePagination,
              }
            : false
        }
        onRow={(record) => ({
          onClick: () => {
            onOpenDetail(record.id);
          },
          className: 'cursor-pointer',
        })}
        onChange={handleTableChange}
        locale={{
          emptyText: 'お問い合わせはまだありません',
        }}
      />
    </div>
  );
};
